import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const TYPE_LABEL = { ferie: '🌴 Ferie', permesso: '🕐 Permesso', malattia: '🤒 Malattia' };
const fmtD = (d) => new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
const num = (v) => (v % 1 === 0 ? String(v) : String(v).replace('.', ','));

const THEMES = {
  dark: {
    box: 'border-slate-700', label: 'text-slate-200', nav: 'text-slate-400 hover:text-white',
    head: 'text-slate-500', row: 'border-slate-700/60 text-slate-300', empty: 'text-slate-500',
    detail: 'text-slate-400', detailHead: 'text-slate-500', dim: 'text-slate-500',
    tileF: 'bg-emerald-900/40 border-emerald-800', valF: 'text-emerald-400',
    tileP: 'bg-sky-900/40 border-sky-800', valP: 'text-sky-400',
    tileM: 'bg-rose-900/40 border-rose-800', valM: 'text-rose-400',
    tileSub: 'text-slate-400',
  },
  light: {
    box: 'border-slate-200 bg-white', label: 'text-slate-800', nav: 'text-slate-400 hover:text-slate-700',
    head: 'text-slate-400', row: 'border-slate-200 text-slate-600', empty: 'text-slate-400',
    detail: 'text-slate-500', detailHead: 'text-slate-400', dim: 'text-slate-400',
    tileF: 'bg-emerald-50 border-emerald-200', valF: 'text-emerald-600',
    tileP: 'bg-sky-50 border-sky-200', valP: 'text-sky-600',
    tileM: 'bg-rose-50 border-rose-200', valM: 'text-rose-600',
    tileSub: 'text-slate-500',
  },
};

// Riepilogo annuale di ferie/permessi/malattie — usato dal dipendente (dark, dati propri)
// e dall'admin in Gestione Personale (light, empId del dipendente scelto)
export const YearSummary = ({ empId = null, light = false }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const T = THEMES[light ? 'light' : 'dark'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = empId
        ? `${API}/hr/employees/${empId}/year-summary?year=${year}`
        : `${API}/hr/my-year-summary?year=${year}`;
      const r = await axios.get(url, { headers: authHeaders() });
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [year, empId]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals || { ferie_days: 0, permesso_hours: 0, malattia_days: 0 };
  const activeMonths = (data?.monthly || []).filter(m => m.ferie_days || m.permesso_hours || m.malattia_days);

  return (
    <div className={`mt-3 border rounded-xl p-4 space-y-3 ${T.box}`} data-testid="hr-year-summary">
      <div className="flex items-center justify-center gap-3">
        <button data-testid="hr-year-prev" onClick={() => setYear(year - 1)} className={T.nav}><ChevronLeft className="h-4 w-4" /></button>
        <span className={`font-bold ${T.label}`} data-testid="hr-year-label">{year}</span>
        <button data-testid="hr-year-next" onClick={() => setYear(year + 1)} className={T.nav}><ChevronRight className="h-4 w-4" /></button>
        {loading && <Loader2 className={`h-4 w-4 animate-spin ${T.dim}`} />}
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className={`border rounded-lg py-2 ${T.tileF}`} data-testid="hr-year-ferie">
          <p className={`text-xl font-bold ${T.valF}`}>{num(t.ferie_days)}</p>
          <p className={`text-[10px] ${T.tileSub}`}>gg ferie godute</p>
        </div>
        <div className={`border rounded-lg py-2 ${T.tileP}`} data-testid="hr-year-permessi">
          <p className={`text-xl font-bold ${T.valP}`}>{num(t.permesso_hours)}</p>
          <p className={`text-[10px] ${T.tileSub}`}>ore permesso</p>
        </div>
        <div className={`border rounded-lg py-2 ${T.tileM}`} data-testid="hr-year-malattia">
          <p className={`text-xl font-bold ${T.valM}`}>{num(t.malattia_days)}</p>
          <p className={`text-[10px] ${T.tileSub}`}>gg malattia</p>
        </div>
      </div>

      {activeMonths.length > 0 ? (
        <table className="w-full text-xs" data-testid="hr-year-months">
          <thead>
            <tr className={T.head}>
              <th className="text-left py-1 font-normal">Mese</th>
              <th className="text-right font-normal">🌴 gg</th>
              <th className="text-right font-normal">🕐 h</th>
              <th className="text-right font-normal">🤒 gg</th>
            </tr>
          </thead>
          <tbody>
            {activeMonths.map(m => (
              <tr key={m.month} className={`border-t ${T.row}`} data-testid={`hr-year-month-${m.month}`}>
                <td className="py-1">{MONTHS[m.month - 1]}</td>
                <td className={`text-right ${T.valF}`}>{m.ferie_days ? num(m.ferie_days) : '—'}</td>
                <td className={`text-right ${T.valP}`}>{m.permesso_hours ? num(m.permesso_hours) : '—'}</td>
                <td className={`text-right ${T.valM}`}>{m.malattia_days ? num(m.malattia_days) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className={`text-sm text-center ${T.empty}`} data-testid="hr-year-empty">Nessuna assenza nel {year}</p>
      )}

      {(data?.absences || []).length > 0 && (
        <div className="space-y-1" data-testid="hr-year-list">
          <p className={`text-[11px] ${T.detailHead}`}>Dettaglio assenze {year}</p>
          {data.absences.map(a => (
            <p key={a.id} className={`text-xs ${T.detail}`}>
              {TYPE_LABEL[a.type] || a.type} · {fmtD(a.date_from)}{a.date_to !== a.date_from ? `–${fmtD(a.date_to)}` : ''}
              {a.time_from ? ` ${a.time_from}-${a.time_to}` : ''}
              <span className={`ml-1 ${T.dim}`}>
                {a.type === 'permesso' ? `(${num(a.hours_count || 0)} h)` : `(${num(a.days_in_year ?? a.days_count ?? 0)} gg)`}
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
